import React, { useEffect, useState } from 'react';
import { Box, InputAdornment, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { CustomButton } from '../../../../components/CustomButton';
import { Link, useLocation } from 'react-router-dom';
import { ROUTES } from '../../../../constants/routes';
import { CustomInput } from '../../../../components/CustomInput';
import { ReactComponent as SearchIcon } from '../../../../assets/images/search.svg';
import { getEventDeclensionForm } from '../../../../utils/getEventDeclension';
import EnhancedTable from '../../../../components/EnhancedTable';
import { PROMOTIONS_HEAD_CELLS } from '../../constants/index';
import {
  archivePromotion,
  getPartnersList,
  getPromotions,
  updatePromotionsPositions
} from '../../../../store/partners/actions';
import { CustomSelect } from '../../../../components/CustomSelect';
import {
  PROMOTIONS_STATUS,
  DISABLED_CONTROL_TOOLTIP,
  PERMISSIONS
} from '../../../../constants/variables';
import { CustomDatePicker } from '../../../../components/CustomDatePicker';
import { FirstTextSelect } from '../../../../components/FirstTextSelcet';
import { CustomModal } from '../../../../components/CustomModal';
import { NotFoundItems } from '../../../../components/NotFoundItems';
import { useDebounce } from 'use-lodash-debounce';
import { visibleBySectionPermission } from '../../../../utils/visibleBySectionPermission';
import { setUsersPermissions } from '../../../../utils/setUsersPermissions';
import { PromotionsTableBodyWithDnD } from '../PromotionsTableBody/PromotionsTableBodyWithDnD';

const styleInput = {
  size: 'small',
  variant: 'outlined'
};

const propsSelect = (name, text, options, value) => ({
  name,
  options,
  value,
  startAdornmentText: <FirstTextSelect text={text} />,
  size: 'small',
  variant: 'outlined'
});

export const PromotionsTab = () => {
  const dispatch = useDispatch();
  const { promotions, fetchingPromotions, partnersList } = useSelector((state) => state.partners);
  const { userPermissions, currentUsersPermissions } = useSelector(
    (state) => state.userPermissions
  );
  const permissions = setUsersPermissions(userPermissions, currentUsersPermissions?.permissions);
  const partners = partnersList.map((item) => ({ label: item?.name, value: item?.id }));
  partners?.unshift({ label: 'Все', value: 'all' });

  const isAllowedToListPartners = visibleBySectionPermission(
    permissions,
    PERMISSIONS.PARTNERS.listPartners.value
  );
  const isAllowedToCreate = visibleBySectionPermission(
    permissions,
    PERMISSIONS.PARTNER.createPartnerPromotions.value
  );
  const isAllowedToList = visibleBySectionPermission(
    permissions,
    PERMISSIONS.PARTNER.listPartnerPromotions.value
  );
  const isAllowedToEdit = visibleBySectionPermission(
    permissions,
    PERMISSIONS.PARTNER.updatePartnerPromotions.value
  );
  const isAllowedToArchive = visibleBySectionPermission(
    permissions,
    PERMISSIONS.PARTNER.archivePartnerPromotions.value
  );
  const isAllowedToRetrieve = visibleBySectionPermission(
    permissions,
    PERMISSIONS.PARTNER.retrievePartnerPromotions.value
  );
  const isAllowedToCopy = visibleBySectionPermission(
    permissions,
    PERMISSIONS.PARTNER.copyPartnerPromotions.value
  );

  const [filter, setFilter] = useState({
    order_by: 'cms_package_name',
    order_type: 'asc',
    search: '',
    status: { value: 'all', label: 'Все' },
    partner_id: { value: 'all', label: 'Все' },
    date_start: '',
    date_end: '',
    page: 0,
    size: 10
  });
  const [isSuccessModal, setIsSuccessModal] = useState(false);
  const [curID, setCurID] = useState('');
  const location = useLocation();

  const debouncedValue = useDebounce(filter.search, 500);

  const dispatchPromotions = () =>
    isAllowedToList &&
    dispatch(
      getPromotions({
        ...filter,
        search: debouncedValue || undefined,
        page: filter.page + 1,
        partner_id: isAllowedToListPartners
          ? filter.partner_id.value !== 'all'
            ? filter.partner_id.value
            : undefined
          : undefined,
        status:
          filter.status.value !== PROMOTIONS_STATUS[0].value ? filter.status.label : undefined,
        date_start: filter?.date_start?.toString().length
          ? (() => {
              const date = new Date(filter?.date_start);
              date.setHours(0, 0, 0);
              return date;
            })()
          : undefined,
        date_end: filter?.date_end?.toString().length
          ? (() => {
              const date = new Date(filter?.date_end);
              date.setHours(23, 59, 59);
              return date;
            })()
          : undefined
      })
    );

  useEffect(
    () => {
      dispatchPromotions();
      !partnersList?.length && isAllowedToListPartners && dispatch(getPartnersList());
    }, // eslint-disable-next-line
    [
      filter.size,
      filter.partner_id,
      filter.date_end,
      filter.page,
      debouncedValue,
      filter.status,
      filter.date_start,
      filter.order_by,
      filter.order_type
    ]
  );

  const handlerOnChange = (e, field) => setFilter({ ...filter, page: 0, [field]: e });

  const dateToString = (date, name) => {
    try {
      return date.toString();
    } catch (e) {
      setFilter((prevState) => ({ ...prevState, [name]: '' }));
    }
  };

  return (
    <Box data-testid="PromotionsTab">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb="24px">
        <CustomButton
          component={Link}
          to={{ pathname: ROUTES.PARTNERS_ADD_PROMOTION, state: { from: location?.pathname } }}
          size="small"
          mr="24px"
          variant="contained"
          disabled={!isAllowedToCreate}
          tooltip={isAllowedToCreate ? null : DISABLED_CONTROL_TOOLTIP}
          onClick={
            isAllowedToCreate ? () => localStorage.setItem('partnersTab', 'PROMOTIONS') : undefined
          }>
          Создать акцию
        </CustomButton>

        <CustomInput
          value={filter.search}
          onChange={(e) => setFilter({ ...filter, search: e.target.value, page: 0 })}
          size="small"
          {...styleInput}
          placeholder="Поиск"
          disabled={!isAllowedToList}
          tooltip={isAllowedToList ? null : DISABLED_CONTROL_TOOLTIP}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
      </Box>

      <Box display="grid" gridTemplateColumns="1fr 1fr 1fr 1fr" gap="16px" data-testid="Grid">
        <CustomSelect
          {...propsSelect('partner_id', 'Партнёры:', partners, filter.partner_id)}
          onChange={(e) => handlerOnChange(e, 'partner_id')}
          disabled={!isAllowedToListPartners || !isAllowedToList}
          tooltip={!isAllowedToListPartners || !isAllowedToList ? null : DISABLED_CONTROL_TOOLTIP}
        />

        <CustomDatePicker
          name="date_start"
          value={filter.date_start}
          onChange={(e) => {
            handlerOnChange(e, 'date_start');
          }}
          fixedPlaceholderText="Дата начала действия:"
          sx={{ width: '335px' }}
          {...styleInput}
          offLastDate={false}
          previousDate
          error={dateToString(filter?.date_start, 'date_start') === 'Invalid Date'}
          disabled={!isAllowedToList}
          tooltip={isAllowedToList ? null : DISABLED_CONTROL_TOOLTIP}
        />

        <CustomDatePicker
          name="date_end"
          value={filter.date_end}
          onChange={(e) => handlerOnChange(e, 'date_end')}
          fixedPlaceholderText="Дата окончания действия:"
          sx={{ width: '370px' }}
          {...styleInput}
          offLastDate={false}
          previousDate
          error={dateToString(filter?.date_end, 'date_end') === 'Invalid Date'}
          disabled={!isAllowedToList}
          tooltip={isAllowedToList ? null : DISABLED_CONTROL_TOOLTIP}
        />

        <CustomSelect
          {...propsSelect('status', 'Статус:', PROMOTIONS_STATUS, filter.status)}
          onChange={(e) => handlerOnChange(e, 'status')}
          disabled={!isAllowedToList}
          tooltip={isAllowedToList ? null : DISABLED_CONTROL_TOOLTIP}
        />
      </Box>

      {promotions?.items?.length && isAllowedToList ? (
        <>
          <Typography color="text.secondary" m="24px 0px">
            Найдено {getEventDeclensionForm(promotions?.items?.length, 'partners_events')}
          </Typography>

          <EnhancedTable
            rows={promotions?.items}
            headCells={
              filter.status.value === 'active'
                ? PROMOTIONS_HEAD_CELLS
                : PROMOTIONS_HEAD_CELLS.filter((item) => item.id !== 'position_index')
            }
            filter={filter}
            total={promotions?.total || 0}
            fetching={fetchingPromotions}
            heightRow={77}
            setFilter={setFilter}>
            <PromotionsTableBodyWithDnD
              data={promotions?.items}
              setData={(data) => {
                dispatch(updatePromotionsPositions(data));
              }}
              isDraggable={filter.status.value === 'active'}
              openModal={() => setIsSuccessModal(true)}
              setCurID={setCurID}
              permissions={{
                edit: isAllowedToEdit,
                retrieve: isAllowedToRetrieve,
                copy: isAllowedToCopy,
                archive: isAllowedToArchive
              }}
            />
          </EnhancedTable>
        </>
      ) : (
        <NotFoundItems
          message={isAllowedToList ? undefined : 'Вам запрещён доступ к просмотру списка акций'}
        />
      )}

      <CustomModal
        opened={isSuccessModal}
        modalClose={() => setIsSuccessModal(false)}
        modalSubmit={() => {
          dispatch(archivePromotion(curID));
          dispatchPromotions();
          setIsSuccessModal(false);
        }}
        text="Вы действительно хотите перевести данную партнёрскую акцию в Архив?"
        cancelBtnName="Отмена"
        submitBtnName="Да"
        data-testid="Warning Modal"
      />
    </Box>
  );
};
